import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  BarChart3,
  Boxes,
  Building2,
  CheckCircle2,
  ClipboardList,
  Clock,
  Download,
  FileText,
  Grid3x3,
  Images,
  MapPin,
  ScrollText,
  Search,
  Settings,
  Sparkles,
  Tag,
  Upload,
  UserCheck,
  Users,
} from 'lucide-react';

import { ROUTES } from '@/constants/routes';

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  minStage: WorkflowStage;
  pmOnly?: boolean;
  requiresComponentization?: boolean;
  dividerAfter?: boolean;
};

export type WorkflowStage = 'collection' | 'qa_review' | 'reconciliation' | 'final_delivery';

export const STAGE_ORDER: WorkflowStage[] = [
  'collection',
  'qa_review',
  'reconciliation',
  'final_delivery',
];

export const STAGE_LABELS: Record<WorkflowStage, string> = {
  collection: 'Collection',
  qa_review: 'QA Review',
  reconciliation: 'Reconciliation',
  final_delivery: 'Final Delivery',
};

export const LOCK_HINTS: Record<WorkflowStage, string> = {
  collection: '',
  qa_review: 'Complete collection first',
  reconciliation: 'Complete QA review first',
  final_delivery: 'Complete reconciliation first',
};

export const NAV_ITEMS: NavItem[] = [
  {
    to: ROUTES.OVERVIEW,
    label: 'Overview',
    icon: BarChart3,
    minStage: 'collection',
    dividerAfter: true,
  },

  { to: ROUTES.IMPORT, label: 'Import FAR', icon: Upload, minStage: 'collection', pmOnly: true },
  { to: ROUTES.LOCATIONS, label: 'Locations', icon: MapPin, minStage: 'collection' },
  { to: ROUTES.BUILDINGS, label: 'Buildings', icon: Building2, minStage: 'collection' },
  {
    to: ROUTES.TEAM,
    label: 'Team',
    icon: Users,
    minStage: 'collection',
    pmOnly: true,
    dividerAfter: true,
  },

  {
    to: ROUTES.ASSIGNMENTS,
    label: 'Assignments',
    icon: UserCheck,
    minStage: 'collection',
    dividerAfter: true,
  },

  { to: ROUTES.REGISTER, label: 'Asset Register', icon: ClipboardList, minStage: 'collection' },
  { to: ROUTES.TAGS, label: 'Tags', icon: Tag, minStage: 'collection', pmOnly: true },

  { to: ROUTES.QA_REVIEW, label: 'QA Review', icon: Search, minStage: 'qa_review' },
  { to: ROUTES.INVENTORY, label: 'Inventory', icon: Images, minStage: 'qa_review' },
  { to: ROUTES.CONFLICTS, label: 'Conflicts', icon: AlertTriangle, minStage: 'qa_review' },
  {
    to: ROUTES.DEFERRED,
    label: 'Deferred',
    icon: Clock,
    minStage: 'qa_review',
    dividerAfter: true,
  },

  { to: ROUTES.RECONCILIATION, label: 'Reconciliation', icon: FileText, minStage: 'qa_review' },
  {
    to: ROUTES.COMPONENTIZATION,
    label: 'Componentization',
    icon: Grid3x3,
    minStage: 'qa_review',
    pmOnly: true,
    requiresComponentization: true,
  },
  { to: ROUTES.FINAL_DELIVERY, label: 'Final Delivery', icon: CheckCircle2, minStage: 'qa_review' },
  {
    to: ROUTES.EXPORTS,
    label: 'Exports',
    icon: Download,
    minStage: 'qa_review',
    pmOnly: true,
    dividerAfter: true,
  },

  {
    to: ROUTES.AI_INSIGHTS,
    label: 'AI Insights',
    icon: Sparkles,
    minStage: 'collection',
    dividerAfter: true,
  },
  {
    to: ROUTES.ACTIVITY_LOG,
    label: 'Activity Log',
    icon: ScrollText,
    minStage: 'collection',
    dividerAfter: true,
  },

  { to: ROUTES.SETUP, label: 'Settings', icon: Settings, minStage: 'collection' },
];

export function isStageUnlocked(
  currentStage: WorkflowStage,
  requiredStage: WorkflowStage
): boolean {
  return STAGE_ORDER.indexOf(currentStage) >= STAGE_ORDER.indexOf(requiredStage);
}

/** Keep so old code can reference unused icons without breaking imports. */
export const _UNUSED_ICONS = [Boxes];
